import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Page } from '@/api/types';
import { Banner, Card, ErrorNote, Spinner, Timestamp } from '@/components/Common';

interface WebhookDelivery {
  id: number;
  orderId: string;
  eventType: string;
  status: 'PENDING' | 'RETRYING' | 'DELIVERED' | 'ABANDONED';
  attempts: number;
  lastError: string | null;
  nextRetryAt: string;
  publishedAt: string | null;
  createdAt: string;
}

const STATUS_TONE: Record<WebhookDelivery['status'], string> = {
  DELIVERED: 'bg-emerald-100 text-emerald-800',
  PENDING: 'bg-ink-100 text-ink-600',
  RETRYING: 'bg-amber-100 text-amber-800',
  ABANDONED: 'bg-red-100 text-red-800',
};

/**
 * A merchant's own webhook delivery log — what the Platform tried to POST to their callback URL
 * for each order event, and whether it arrived. Read-only: the callback URL and signing secret
 * are still set by an admin (AdminMerchantController), not from here.
 */
export function WebhooksPage() {
  const { data: config } = useQuery({
    queryKey: ['webhook-config'],
    queryFn: () => api.get<{ configured: boolean }>('/api/v1/webhooks/config'),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['webhook-deliveries'],
    queryFn: () => api.get<Page<WebhookDelivery>>('/api/v1/webhooks/deliveries?size=100'),
    refetchInterval: 15_000,
  });

  return (
    <div className="space-y-4">
      {config && !config.configured && (
        <Banner tone="warn">
          No callback URL is configured for your account, so order events have nowhere to be
          delivered. Ask an admin to set one up if you want webhook notifications.
        </Banner>
      )}

      <Card title="Webhook deliveries">
        {isLoading && <Spinner />}
        {error && <ErrorNote error={error} />}
        {data && (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-ink-200">
              <tr>
                <th className="th">Event</th>
                <th className="th">Order</th>
                <th className="th">Status</th>
                <th className="th text-right">Attempts</th>
                <th className="th">Error</th>
                <th className="th">Created</th>
                <th className="th">Delivered / next retry</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {data.content.map((d) => (
                <tr key={d.id}>
                  <td className="td mono">{d.eventType}</td>
                  <td className="td mono text-xs text-ink-500">{d.orderId.slice(0, 8)}…</td>
                  <td className="td">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_TONE[d.status]}`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="td tabular text-right">{d.attempts}</td>
                  <td className="td max-w-xs truncate text-ink-600" title={d.lastError ?? ''}>
                    {d.lastError ?? '—'}
                  </td>
                  <td className="td"><Timestamp value={d.createdAt} /></td>
                  <td className="td">
                    {d.status === 'DELIVERED' && d.publishedAt
                      ? <Timestamp value={d.publishedAt} />
                      : d.status === 'ABANDONED' ? '—'
                      : <Timestamp value={d.nextRetryAt} />}
                  </td>
                </tr>
              ))}
              {data.content.length === 0 && (
                <tr><td className="td py-8 text-center text-ink-500" colSpan={7}>
                  Nothing delivered yet — events appear here as your orders complete.
                </td></tr>
              )}
            </tbody>
          </table>
          </div>
        )}
        <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
          A failed delivery retries with backoff for about a day before it's abandoned; abandoning
          a delivery never affects the order itself, only the notification.
        </p>
      </Card>
    </div>
  );
}
